import {useEffect, useMemo, useState} from 'react'
import {Link, Navigate, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable} from '@/components/data-table'
import {KairosMark} from '@/components/kairos-mark'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PersonasDialogs} from '@/components/bateria/personas-dialogs'
import {EvaluacionLifecycleDialogs} from '@/components/bateria/evaluacion-lifecycle-dialogs'
import {PersonasToolbar} from '@/components/bateria/personas-toolbar'
import {columnasDePersonas} from '@/components/bateria/personas-columnas'
import {BarraEstado, type AnclaGestion} from '@/components/bateria/barra-estado'
import {GestionTab, type VistaGestion} from '@/components/bateria/gestion-tab'
import {ResultadosTab} from '@/components/bateria/resultados-tab'
import {ValidacionResultado} from '@/components/bateria/validacion-resultado'
import {QuitarValidacionResultado} from '@/components/bateria/quitar-validacion-resultado'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {PorQue} from '@/components/ui/por-que'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {
  openFormaPdf,
  useCerrarEvaluacion,
  useCierreResumen,
  useEntregarEvaluacion,
  useEvaluacionDetail,
  useImprimirFormasPdf,
  useIngestas,
  useQuitarValidacionLote,
  useRevisionResumen,
  useValidarLote,
  type BateriaForma,
  type BateriaPaquete,
  type QuitarValidacionMasiva,
  type ValidacionMasiva,
} from '@/hooks/bateria/use-bateria'
import {
  coincideEstado,
  ESTADO_ORDER,
  INCOMPLETO_FILTER,
  LISTO_FILTER,
  resumirLotes,
  type EstadoFilter,
} from '@/lib/bateria'
import {destinoDeSeleccion, MAX_PAQUETES_EN_URL} from '@/lib/bateria-seleccion'
import {useAvisoLecturaLista} from '@/hooks/bateria/use-aviso-lectura'
import {marcarErrorComoComunicado, translateApiError} from '@/lib/errors'
import {formatDate} from '@/lib/risk'
import {normalize} from '@/lib/utils'

const TAB_KEYS = ['gestion', 'personas', 'resultados'] as const
type TabKey = (typeof TAB_KEYS)[number]

/** Grafías viejas de `?tab=` que aún llegan en enlaces guardados; se traducen reescribiendo la URL. */
const TAB_VIEJAS: Record<string, {tab: TabKey; sub?: VistaGestion; ancla?: AnclaGestion}> = {
  paquetes: {tab: 'personas'},
  escaneos: {tab: 'gestion', ancla: 'lote-escaneos'},
  bitacora: {tab: 'gestion', sub: 'bitacora'},
  cuarentena: {tab: 'gestion'},
}

function parseTab(raw: string | null): TabKey | null {
  return TAB_KEYS.includes(raw as TabKey) ? (raw as TabKey) : null
}

/** En un `setTimeout` porque la pestaña se monta en el commit siguiente al cambio de `?tab=`. */
function irAlAncla(ancla: AnclaGestion, focoTestId?: string) {
  window.setTimeout(() => {
    document.getElementById(ancla)?.scrollIntoView({behavior: 'smooth', block: 'start'})
    if (focoTestId) document.querySelector<HTMLElement>(`[data-testid="${focoTestId}"]`)?.focus()
  }, 0)
}

const PAGE_SIZE = 10

export function AdminBateriaEvaluacionPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {businessId, evaluacionId} = useParams<{businessId: string; evaluacionId: string}>()
  const isSystemUser = useIsSystemUser()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [adding, setAdding] = useState(false)
  const [addingPersona, setAddingPersona] = useState(false)
  const [reasignando, setReasignando] = useState<BateriaPaquete | null>(null)
  const [retirando, setRetirando] = useState<BateriaPaquete[] | null>(null)
  const [cambiandoForma, setCambiandoForma] = useState<BateriaPaquete | null>(null)
  const [corrigiendoIdentidad, setCorrigiendoIdentidad] = useState<BateriaPaquete | null>(null)
  const [viendoHistorialPaginas, setViendoHistorialPaginas] = useState<BateriaPaquete | null>(null)
  const [reprocesando, setReprocesando] = useState<BateriaPaquete | null>(null)
  const [viendoResultado, setViendoResultado] = useState<BateriaPaquete | null>(null)
  const [viendoFicha, setViendoFicha] = useState<BateriaPaquete | null>(null)
  const [printError, setPrintError] = useState<string | null>(null)
  const [cerrando, setCerrando] = useState(false)
  const [entregando, setEntregando] = useState(false)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)
  // La pestaña vive en `?tab=` para que «atrás» desde revisión o digitación la restaure.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabCrudo = searchParams.get('tab')
  const tab = parseTab(tabCrudo)
  const vistaGestion: VistaGestion = searchParams.get('sub') === 'bitacora' ? 'bitacora' : 'trabajo'

  useEffect(() => {
    const vieja = tabCrudo === null ? undefined : TAB_VIEJAS[tabCrudo]
    if (!vieja) return
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', vieja.tab)
        if (vieja.sub) params.set('sub', vieja.sub)
        return params
      },
      {replace: true}
    )
    if (vieja.ancla) irAlAncla(vieja.ancla)
  }, [tabCrudo, setSearchParams])

  function setTab(next: TabKey, sub?: VistaGestion) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        if (next === 'gestion' && sub) params.set('sub', sub)
        else params.delete('sub')
        return params
      },
      {replace: true}
    )
  }

  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<EstadoFilter>('')
  const [filtroForma, setFiltroForma] = useState<BateriaForma | ''>('')
  const [page, setPage] = useState(1)
  const {data: detail} = useEvaluacionDetail(businessId, evaluacionId, isSystemUser)
  const {data: ingestas} = useIngestas(businessId, evaluacionId, isSystemUser)
  const cerrar = useCerrarEvaluacion(businessId ?? '', evaluacionId ?? '')
  const entregar = useEntregarEvaluacion(businessId ?? '', evaluacionId ?? '')
  const imprimirSeleccionados = useImprimirFormasPdf(businessId ?? '', evaluacionId ?? '')
  const validarSeleccionados = useValidarLote(businessId ?? '', evaluacionId ?? '')
  const quitarValidacionSeleccionados = useQuitarValidacionLote(businessId ?? '', evaluacionId ?? '')
  const [seleccionError, setSeleccionError] = useState<string | null>(null)
  const [validacionSeleccion, setValidacionSeleccion] = useState<ValidacionMasiva | null>(null)
  const [confirmandoQuitarValidacion, setConfirmandoQuitarValidacion] = useState<BateriaPaquete[] | null>(null)
  const [quitarValidacionResultado, setQuitarValidacionResultado] = useState<QuitarValidacionMasiva | null>(null)
  const {data: cierreResumen} = useCierreResumen(businessId, evaluacionId, cerrando)

  const paquetes = useMemo(() => detail?.paquetes ?? [], [detail])
  const jobs = useMemo(() => ingestas?.items ?? [], [ingestas])
  const lotes = useMemo(() => resumirLotes(jobs, Date.now()), [jobs])
  // Un lote detenido cuenta como no-corriendo: sus paquetes se ven «Incompleto» y los controles se liberan.
  const batchRunning = lotes.corriendo && !lotes.detenido
  const {data: resumenRevision, refetch: refrescarResumen} = useRevisionResumen(
    businessId,
    evaluacionId,
    isSystemUser,
    detail?.evaluacion.fase
  )
  useAvisoLecturaLista(evaluacionId, ingestas?.items, refrescarResumen, () =>
    navigate(`/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}/revision`)
  )

  const matching = useMemo(() => {
    const needle = normalize(search)
    return paquetes.filter(
      (p) =>
        (!needle || normalize(p.employee_name).includes(needle)) &&
        coincideEstado(p, filtroEstado, batchRunning) &&
        (!filtroForma || p.forma === filtroForma)
    )
  }, [paquetes, search, filtroEstado, filtroForma, batchRunning])
  const rows = useMemo(() => matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [matching, page])

  if (!isSystemUser || !businessId || !evaluacionId) return <Navigate to="/" replace />

  const estado = detail?.evaluacion.estado ?? 'abierta'
  const readOnly = estado !== 'abierta'

  const selectedPaquetes = paquetes.filter((p) => selected.has(p.id))
  const hasValidated = paquetes.some((p) => p.estado === 'validado')
  const validadosCount = paquetes.filter((p) => p.estado === 'validado').length

  const effectiveTab: TabKey = tab ?? (hasValidated ? 'resultados' : 'personas')

  const total = matching.length
  const allMatchingSelected = matching.length > 0 && matching.every((p) => selected.has(p.id))

  function resetPage() {
    setPage(1)
  }

  const lotePath = `/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}`

  function irAGestion(ancla: AnclaGestion, focoTestId?: string) {
    setTab('gestion')
    irAlAncla(ancla, focoTestId)
  }

  function abrirAgregarPaquetes() {
    setTab('personas')
    setAdding(true)
  }

  function selectAllMatching() {
    setSelected(new Set(matching.map((p) => p.id)))
  }

  async function print(paquete: BateriaPaquete) {
    setPrintError(null)
    try {
      await openFormaPdf(businessId!, paquete.id)
    } catch (err) {
      setPrintError(translateApiError(err))
    }
  }

  function validarPaquetes(paqueteIds: string[]) {
    setSeleccionError(null)
    setValidacionSeleccion(null)
    validarSeleccionados.mutate(paqueteIds, {
      onSuccess: (data) => {
        setValidacionSeleccion(data)
        setSelected(new Set())
      },
      onError: (err) => setSeleccionError(translateApiError(err)),
    })
  }

  async function imprimirSeleccion() {
    setSeleccionError(null)
    try {
      await imprimirSeleccionados.mutateAsync({paqueteIds: selectedPaquetes.map((p) => p.id)})
    } catch (err) {
      setSeleccionError(translateApiError(err))
    }
  }

  function validarSeleccion() {
    validarPaquetes(selectedPaquetes.map((p) => p.id))
  }

  /** Manda toda la selección: el servidor devuelve omitidas, con motivo, las que no estaban validadas. */
  function confirmarQuitarValidacionSeleccion() {
    if (!confirmandoQuitarValidacion) return
    setSeleccionError(null)
    setQuitarValidacionResultado(null)
    quitarValidacionSeleccionados.mutate(
      confirmandoQuitarValidacion.map((p) => p.id),
      {
        onSuccess: (data) => {
          setQuitarValidacionResultado(data)
          setConfirmandoQuitarValidacion(null)
          setSelected(new Set())
        },
        onError: (err) => {
          setSeleccionError(translateApiError(err))
          setConfirmandoQuitarValidacion(null)
        },
      }
    )
  }

  // `revision/cola` acepta `paquete_id` repetido, así que la cola se acota a las seleccionadas.
  function revisarSeleccionadas() {
    setSeleccionError(null)
    const destino = destinoDeSeleccion(
      selectedPaquetes.map((p) => p.id),
      paquetes.length
    )
    if (destino.tipo === 'demasiadas') {
      setSeleccionError(
        t('admin.bateria.detail.seleccionDemasiadas', {max: MAX_PAQUETES_EN_URL, sobran: destino.sobran})
      )
      return
    }
    const base = `${lotePath}/revision`
    navigate(destino.query ? `${base}?${destino.query}` : base)
  }

  function confirmarCerrar() {
    setLifecycleError(null)
    cerrar.mutate(undefined, {
      onSuccess: () => setCerrando(false),
      // `useCerrarEvaluacion` no declara `manejaError` porque su otro llamador no lo maneja.
      onError: (err) => {
        marcarErrorComoComunicado(err)
        setLifecycleError(t('admin.bateria.lifecycle.cerrarError'))
      },
    })
  }

  function confirmarEntregar() {
    setLifecycleError(null)
    entregar.mutate(undefined, {
      onSuccess: () => setEntregando(false),
      onError: () => setLifecycleError(t('admin.bateria.lifecycle.entregarError')),
    })
  }

  // Guarda defensiva: las celdas que ofrecen esta puerta ya se esconden mientras el lote lee.
  const irAUbicar = () => {
    if (batchRunning) return
    navigate(`${lotePath}/revision?ir_a=ubicar`)
  }

  const columns = columnasDePersonas({
    t,
    businessId,
    evaluacionId,
    lotes,
    batchRunning,
    readOnly,
    validarPending: validarSeleccionados.isPending,
    onIrAUbicar: irAUbicar,
    onIrAPendientes: (p) => navigate(`${lotePath}/revision?paquete_id=${p.id}`),
    onValidar: (p) => validarPaquetes([p.id]),
    onRevisar: (p) => navigate(`${lotePath}/revision?paquete_id=${p.id}`),
    // `filtros=1` aterriza con el panel de filtros abierto.
    onAbrirEnCola: (p) => navigate(`${lotePath}/revision?paquete_id=${p.id}&filtros=1`),
    onDigitar: (p) => navigate(`${lotePath}/paquetes/${p.id}/digitacion`),
    onVerResultado: setViendoResultado,
    onVerFicha: setViendoFicha,
    onImprimir: print,
    onReprocesar: setReprocesando,
    onReasignar: setReasignando,
    onCorregirIdentidad: setCorrigiendoIdentidad,
    onCambiarForma: setCambiandoForma,
    onRetirar: (p) => setRetirando([p]),
    onVerHistorialPaginas: setViendoHistorialPaginas,
  })

  return (
    <div className="space-y-5" data-testid="bateria-evaluacion">
      <div>
        <Link to="/admin/bateria" className="font-mono text-[11px] text-primary hover:underline">
          {t('admin.bateria.detail.back')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-foreground">
          <KairosMark />
          <h1 className="font-heading text-xl">{t('admin.bateria.detail.title')}</h1>
        </div>
        {detail && (
          <>
            <p className="mt-1.5 font-mono text-[11px] text-muted-foreground">
              <span data-testid="evaluacion-estado">{t(`admin.bateria.evaluacionEstado.${estado}`)}</span>
              {' · '}
              {formatDate(detail.evaluacion.fecha_inicio)}
              {' · '}
              {detail.evaluacion.psicologo_name}
            </p>
            {detail.evaluacion.notas && (
              <p className="mt-1 truncate text-sm text-muted-foreground" data-testid="evaluacion-notas">
                {detail.evaluacion.notas.split('\n')[0]}
              </p>
            )}
          </>
        )}
      </div>

      {!detail ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <BarraEstado
            businessId={businessId}
            evaluacionId={evaluacionId}
            evaluacion={detail.evaluacion}
            paquetes={paquetes}
            jobs={jobs}
            lotes={lotes}
            resumen={resumenRevision}
            onAgregarPaquetes={abrirAgregarPaquetes}
            onIrAGestion={irAGestion}
            onIrAlInforme={() => navigate(`${lotePath}/informe`)}
          />
          {lifecycleError && <p className="text-xs text-destructive">{lifecycleError}</p>}
          {printError && <p className="text-xs text-destructive">{printError}</p>}

          <Tabs value={effectiveTab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList>
              <TabsTrigger value="gestion" data-testid="tab-gestion">
                {t('admin.bateria.detail.tabGestion')}
              </TabsTrigger>
              <TabsTrigger value="personas" data-testid="tab-personas">
                {t('admin.bateria.detail.tabPaquetes')} ·{' '}
                {t('admin.bateria.detail.tabPaquetesCount', {count: paquetes.length})}
              </TabsTrigger>
              <TabsTrigger value="resultados" data-testid="tab-resultados">
                {t('admin.bateria.detail.tabResultados')} ·{' '}
                {t('admin.bateria.detail.tabResultadosCount', {count: validadosCount})}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="gestion" className="pt-4">
              <GestionTab
                businessId={businessId}
                evaluacionId={evaluacionId}
                evaluacion={detail.evaluacion}
                paquetes={paquetes}
                jobs={jobs}
                lotes={lotes}
                readOnly={readOnly}
                vista={vistaGestion}
                onVistaChange={(vista) => setTab('gestion', vista)}
                selectedPaqueteIds={selectedPaquetes.map((p) => p.id)}
                onCerrar={() => setCerrando(true)}
                onEntregar={() => setEntregando(true)}
              />
            </TabsContent>

            <TabsContent value="personas" className="space-y-4 pt-4">
              {paquetes.length === 0 ? (
                <div className="surface">
                  <ListEmptyState
                    page
                    title={t('admin.bateria.detail.emptyTitle')}
                    action={
                      readOnly ? undefined : (
                        <Button variant="outline" data-testid="agregar-paquetes" onClick={() => setAdding(true)}>
                          {t('admin.bateria.detail.agregar')}
                        </Button>
                      )
                    }
                  />
                </div>
              ) : (
                <>
                  <p
                    className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
                    data-testid="banda-cobertura">
                    {t('admin.bateria.bandaDeVerdad.cobertura', {
                      convocados: detail.evaluacion.convocados,
                      aplicados: detail.evaluacion.aplicados,
                      noAsistieron: detail.evaluacion.no_asistieron,
                      retiradosAdministrativos: detail.evaluacion.retirados_administrativos,
                    })}
                    <PorQue
                      label={t('admin.bateria.bandaDeVerdad.coberturaPorque')}
                      testId="banda-cobertura-help"
                      contenidoTestId="banda-cobertura-porque">
                      {t('admin.bateria.bandaDeVerdad.coberturaHelp')}
                    </PorQue>
                  </p>
                  <ListCard
                    toolbar={
                      <PersonasToolbar
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next)
                          resetPage()
                        }}
                        estadoFiltroSlot={
                          <select
                            value={filtroEstado}
                            onChange={(e) => {
                              setFiltroEstado(e.target.value as EstadoFilter)
                              resetPage()
                            }}
                            aria-label={t('admin.bateria.detail.filtroEstado')}
                            data-testid="paquetes-filtro-estado"
                            className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
                            <option value="">{t('admin.bateria.detail.filtroEstadoTodos')}</option>
                            {ESTADO_ORDER.map((estado) => (
                              <option key={estado} value={estado}>
                                {t(`admin.bateria.paqueteEstado.${estado}`)}
                              </option>
                            ))}
                            <option value={LISTO_FILTER}>{t('admin.bateria.paqueteEstado.listoParaValidar')}</option>
                            <option value={INCOMPLETO_FILTER}>{t('admin.bateria.detail.estadoIncompleto')}</option>
                          </select>
                        }
                        filtroForma={filtroForma}
                        onFiltroFormaChange={(next) => {
                          setFiltroForma(next)
                          resetPage()
                        }}
                        selected={selected}
                        matchingCount={matching.length}
                        allMatchingSelected={allMatchingSelected}
                        onSelectAllMatching={selectAllMatching}
                        onDeselectAll={() => setSelected(new Set())}
                        readOnly={readOnly}
                        selectedPaquetes={selectedPaquetes}
                        imprimirPending={imprimirSeleccionados.isPending}
                        onImprimirSeleccion={imprimirSeleccion}
                        validarPending={validarSeleccionados.isPending}
                        onValidarSeleccion={validarSeleccion}
                        quitarValidacionPending={quitarValidacionSeleccionados.isPending}
                        onQuitarValidacionSeleccion={setConfirmandoQuitarValidacion}
                        onRetirarSeleccion={setRetirando}
                        onRevisarSeleccionadas={revisarSeleccionadas}
                        onAgregarPaquetes={() => setAdding(true)}
                        onAgregarPersona={() => setAddingPersona(true)}
                      />
                    }
                    footer={<PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />}>
                    {seleccionError && <p className="px-5 pt-3 text-xs text-destructive">{seleccionError}</p>}
                    {validacionSeleccion && (
                      <div className="px-5 pt-3">
                        <ValidacionResultado
                          resultado={validacionSeleccion}
                          nombreDePaquete={(id) =>
                            paquetes.find((p) => p.id === id)?.employee_name ??
                            t('admin.bateria.validacion.personaDesconocida')
                          }
                          arreglarHref={`${lotePath}?tab=personas`}
                        />
                      </div>
                    )}
                    {quitarValidacionResultado && (
                      <div className="px-5 pt-3">
                        <QuitarValidacionResultado
                          resultado={quitarValidacionResultado}
                          nombreDePaquete={(id) =>
                            paquetes.find((p) => p.id === id)?.employee_name ??
                            t('admin.bateria.validacion.personaDesconocida')
                          }
                        />
                      </div>
                    )}
                    <DataTable
                      columns={columns}
                      rows={rows}
                      rowKey={(p) => p.id}
                      selection={{selected, onChange: setSelected}}
                      emptyState={<ListEmptyState title={t('admin.bateria.detail.sinResultados')} />}
                    />
                  </ListCard>
                </>
              )}
            </TabsContent>

            <TabsContent value="resultados" className="space-y-4 pt-4">
              <ResultadosTab
                businessId={businessId}
                evaluacionId={evaluacionId}
                evaluacion={detail.evaluacion}
                hasValidated={hasValidated}
                totalPaquetes={paquetes.length}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      <PersonasDialogs
        businessId={businessId}
        evaluacionId={evaluacionId}
        paquetes={paquetes}
        lotes={lotes}
        adding={adding}
        onAddingChange={setAdding}
        addingPersona={addingPersona}
        onAddingPersonaChange={setAddingPersona}
        retirando={retirando}
        onRetirandoChange={setRetirando}
        reasignando={reasignando}
        onReasignandoChange={setReasignando}
        cambiandoForma={cambiandoForma}
        onCambiandoFormaChange={setCambiandoForma}
        corrigiendoIdentidad={corrigiendoIdentidad}
        onCorrigiendoIdentidadChange={setCorrigiendoIdentidad}
        viendoHistorialPaginas={viendoHistorialPaginas}
        onViendoHistorialPaginasChange={setViendoHistorialPaginas}
        reprocesando={reprocesando}
        onReprocesandoChange={setReprocesando}
        viendoResultado={viendoResultado}
        onViendoResultadoChange={setViendoResultado}
        viendoFicha={viendoFicha}
        onViendoFichaChange={setViendoFicha}
      />

      <ConfirmDialog
        open={!!confirmandoQuitarValidacion}
        onOpenChange={(open) => !open && setConfirmandoQuitarValidacion(null)}
        title={t('admin.bateria.detail.quitarValidacionSeleccionConfirmTitle')}
        description={t('admin.bateria.detail.quitarValidacionSeleccionConfirmDesc', {
          count: (confirmandoQuitarValidacion ?? []).filter((p) => p.estado === 'validado').length,
        })}
        confirmLabel={t('admin.bateria.detail.quitarValidacionSeleccionConfirmButton')}
        cancelLabel={t('common.cancel')}
        pending={quitarValidacionSeleccionados.isPending}
        onConfirm={confirmarQuitarValidacionSeleccion}
      />

      <EvaluacionLifecycleDialogs
        cerrando={cerrando}
        onCerrandoChange={setCerrando}
        cierreResumen={cierreResumen}
        cerrarPending={cerrar.isPending}
        onConfirmarCerrar={confirmarCerrar}
        entregando={entregando}
        onEntregandoChange={setEntregando}
        entregarPending={entregar.isPending}
        onConfirmarEntregar={confirmarEntregar}
      />
    </div>
  )
}
